import { TypeRole, TypeTitre, UserFormData } from 'core/models';
import { utilisateurService } from 'core/services';
import { useUtilisateurFromContext } from 'features/utilisateurs/contexts/UtilisateurFormProvider';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

export const FormValidation = (selectedUser, mode) => {
    const { t } = useTranslation();
    const { loading, setIsCheckingEmail, isValidatedEmail, setIsValidatedEmail } = useUtilisateurFromContext();

    const passwordValidationSchema = Yup.object().shape({
        confirmation: Yup.string().oneOf([Yup.ref('password'), null], 'La confirmation doit être identique au mot de passe'),
        password: Yup.string()
            .required()
            .test('password', 'Le mot de passe doit contenir entre 12 et 15 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial (-!@#$%^&*)', function (value) {
                if (mode !== 'newUser') return true;
                if (!!value) {
                    const schema = Yup.string().matches(
                        // eslint-disable-next-line no-useless-escape
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[-!@#\$%\^&\*])(?=.{12,15})/,
                    );
                    return schema.isValidSync(value);
                }
                return true; // -
            })
            .test({
                message: t('Maximum15 caractères'),
                test: (objects: any) => {
                    if (objects?.length > 15) return false;
                    return true;
                },
            }),
    });

    const validationSchema: Yup.SchemaOf<UserFormData> = Yup.object().shape({
        id: Yup.number().required(),
        codeGestionnaire: Yup.string(),
        titre: Yup.mixed<TypeTitre>()
            .required()
            .test('checkTitre', 'Champ requis', (value) => {
                const titres = ['M', 'MME'];
                return titres.includes(value);
            }),
        nom: Yup.string()
            .required()
            .matches(/^[a-zA-Z0-9- ]+$/, 'Veuillez saisir uniquement des caractères alphanumériques ou tirets pour ce champ'),
        prenom: Yup.string()
            .required()
            .matches(/^[a-zA-Z0-9- ]+$/, 'Veuillez saisir uniquement des caractères alphanumériques ou tirets pour ce champ'),
        telephone: Yup.string().required(),
        email: Yup.string()
            .required()
            .email('Email invalide')
            .test(
                'checkEmailUnique',
                'Cet email est déjà utilisé',
                (
                    value,
                    context, // TODO :: translation
                ) => {
                    //if (!Yup.string().email().isValidSync(value)) return true;
                    if (isValidatedEmail || loading) return true;
                    setIsCheckingEmail(true);
                    return utilisateurService
                        .isEmailUnique(value, context.parent.id)
                        .then(async (res) => {
                            const { isEmailUnique } = await res;
                            if (isEmailUnique) setIsValidatedEmail(() => true);
                            return isEmailUnique;
                        })
                        .catch(() => {
                            toast.error("Une erreur est survenue durant la vérification de l'email");
                        })
                        .finally(() => {
                            setIsCheckingEmail(() => false);
                        });
                },
            ),

        fonction: Yup.string().required(),
        role: Yup.mixed().oneOf(Object.values(TypeRole)),
        responsable: Yup.object()
            .nullable(true)
            .shape({
                id: Yup.number().required('Responsable ID is required'),
                prenom: Yup.string().required('Responsable first name is required'),
                nom: Yup.string().required('Responsable last name is required'),
            })
            .default(null)
            .nullable()
            .test('responsable', t('utilisateur.validation.responsableId'), (value, context) => {
                if (mode === 'newUser') return true;
                if (context.parent.role === 'UTILISATEUR' && !value) return false;
                return true;
            }),
        accesWeb: Yup.string().test('checkAccesWeb', 'Champ requis', (value) => {
            if (mode == 'newUser') return true;
            if (value === '') return false;
            return true;
        }),

        login: Yup.string(),
        //.required()
        // .test(
        //     'checkLoginUnique',
        //     'Cet identifiant est invalide',
        //     (
        //         value,
        //         context, // TODO :: translation
        //     ) => {
        //         if (context.parent.email !== '' && context.parent.email !== value) return false;
        //         return true;
        //     },
        // ),

        codeGestionnaireMultiple: Yup.array(
            Yup.object({
                id: Yup.string(),
                idSouscrip: Yup.string().required(),
                codeGestionnaire: Yup.string().required(),
            }),
        ).min(0),
        perimetre: Yup.object().shape({
            risques: Yup.object()
                .shape({
                    sante: Yup.boolean(),
                    prevoyance: Yup.boolean(),
                })
                .test({
                    message: t('yup.validators.risques'),
                    test: (objects: any) => {
                        if (selectedUser.role === 'ADMIN') return true;
                        if (!objects) return false;
                        const values = Object.values(objects);
                        return values.includes(true);
                    },
                }),
            souscripteurs: Yup.array()
                .of(
                    Yup.object().shape({
                        value: Yup.string().required(),
                        label: Yup.string().required(),
                    }),
                )
                .test({
                    message: t('yup.validators.required'),
                    test: (objects: any) => {
                        if (selectedUser.role === 'ADMIN') return true;
                        return objects?.length ? true : false;
                    },
                }),
            colleges: Yup.array()
                .of(
                    Yup.object().shape({
                        value: Yup.string().required(),
                        label: Yup.string().required(),
                    }),
                )
                .test({
                    message: t('yup.validators.required'),
                    test: (objects: any) => {
                        if (selectedUser.role === 'ADMIN') return true;
                        return objects?.length ? true : false;
                    },
                }),
            excludeVIP: Yup.boolean(),
        }),

        roles: Yup.array().test({
            message: t('yup.validators.droits'), // Custom error message
            test: (objects: any) => {
                // Check if the selected user's role is 'ADMIN', and if so, return true (no validation needed)
                if (selectedUser.role === 'ADMIN') return true;

                // Check if the 'objects' array is empty or undefined, and if so, return false
                if (!objects || objects.length === 0) return false;

                // Filter out roles that are not 'ROLE_RESPONSABLE' or 'ROLE_UTILISATEUR'
                const filteredRoles = objects.filter((role) => role !== 'ROLE_RESPONSABLE' && role !== 'ROLE_UTILISATEUR');

                // Return true if there are filtered roles (valid), otherwise return false (invalid)
                return filteredRoles.length > 0;
            },
        }),
        confirmation: Yup.string(),
        password: Yup.string(),
        alertBia: Yup.object().shape({
            id: Yup.number().nullable(),
            actif: Yup.boolean(),
            frequence: Yup.string().nullable(),
            lastNotification: Yup.string().nullable(),
        }),
    });

    if (mode === 'newUser') {
        const mergedSchema = validationSchema.concat(passwordValidationSchema);
        return mergedSchema;
    } else {
        return validationSchema;
    }
};
